import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  defaultDateRange,
  parseBangkokDateRangeQuery,
} from "@/lib/format";

type Range = { dateStart: string; dateEnd: string };

type Ctx = {
  dateStart: string;
  dateEnd: string;
  setRange: (next: Range) => void;
};

const DateRangeContext = createContext<Ctx | null>(null);

/**
 * โหลดครั้งแรก: มี `?dateStart=&dateEnd=` ใช้ตาม URL — ไม่งั้นใช้ 7 วันล่าสุด
 * ไม่ restore จาก localStorage/sessionStorage เพื่อไม่ให้ช่วงยาว (~31 วัน) ค้างหลังรีเฟรช
 * ช่วงที่ผู้ใช้เลือกยังอยู่ระหว่างสลับหน้าใน SPA ผ่าน state ของ Provider
 */
function readInitialRange(): Range {
  if (typeof window === "undefined") return defaultDateRange();
  const params = new URLSearchParams(window.location.search);
  const parsed = parseBangkokDateRangeQuery(params.get("dateStart"), params.get("dateEnd"));
  if (parsed) return parsed;
  return defaultDateRange();
}

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<Range>(readInitialRange);

  const setRange = useCallback((next: Range) => {
    setState(next);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const parsed = parseBangkokDateRangeQuery(params.get("dateStart"), params.get("dateEnd"));
    if (parsed) {
      setState(parsed);
    }
  }, [location.search]);

  const value = useMemo(
    () => ({ dateStart: state.dateStart, dateEnd: state.dateEnd, setRange }),
    [state.dateStart, state.dateEnd, setRange]
  );

  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

export function useDateRange(): Ctx {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error("useDateRange must be used within DateRangeProvider");
  }
  return ctx;
}
